import {css, Tab, TabProps, Tabs, Typography, useTheme} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import {DlMuiPopper, getIconComponentByName, GetStylesFunction, useMuiAnchor} from '@wallaby-ui/core';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

export type LightFilterTabDef = {
  /** Stable id stored in the page state (URL / localStorage) as the active tab. */
  id: string;
  /** Translated label. */
  label: string;
  /** Icon from the core icon set (src/assets/icons), rendered before the label. */
  iconName: string;
  /** Active filters inside this tab; the badge is hidden at 0. */
  count?: number;
  /** Kept in the strip definition but not rendered (display: none), e.g. a tab whose filters are not implemented yet. */
  hidden?: boolean;
};

type Props = {
  tabs: LightFilterTabDef[];
  activeTab: string;
  onChange: (tabId: string) => void;
  dataName?: string;
  /** Always drop the labels of the inactive tabs (icon + badge only), not only when the strip does not fit -
   * the right panel of the hospitalization detail is drawn this way (PDW-182). */
  compact?: boolean;
  /** Show the badge at 0 as well, so a tab says "nothing" instead of hiding its count. */
  showZeroCount?: boolean;
  /** 'filter' is the tabbed filter panel look; 'panel' the right-panel strip of the alerts design: 19px outline
   * icons, 13.5px labels, 9px pills with a 6px gap, and a 5px-radius badge with grey text. */
  variant?: 'filter' | 'panel';
  /** The pills at the size of the page tabs (.dl-tab-row, e.g. Patient / Documents): the theme's tab typography, icons
   * and badges scaled to it and the --horizontal-tab-height of the header buttons beside them (PDW-421). */
  pageTabSize?: boolean;
};

type TooltipTabProps = TabProps & {
  /** The app tooltip of a tab without its whole label (its name); none on a tab that shows it. */
  tooltip?: string;
};
/**
 * A Tab with its own app tooltip (not the browser's title). Each tab keeps its own tooltip state: with one shared between
 * the tabs, moving straight onto the next tab let the previous tab's "hover landed elsewhere" check close the new tooltip.
 * MUI Tabs injects its props (value, selected, onChange...) into this wrapper, which hands them to the Tab; the popper is
 * a portal, so the Tab stays the only DOM child the strip measures and moves the focus through.
 */
const TooltipTab: React.FC<TooltipTabProps> = (props) => {
  const {tooltip, onClick, ...tabProps} = props;
  // the selected tab keeps the focus after its click, which must not keep its (cut label's) tooltip from showing
  const {mouseEventEnter, mouseEventLeave, open, anchorEl, forceCloseTooltip} = useMuiAnchor(Boolean(tooltip), {ignoreFocus: true});
  return (
    <>
      <Tab
        {...tabProps}
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        onClick={(event) => {
          forceCloseTooltip();
          onClick?.(event);
        }}
      />
      <DlMuiPopper hasTooltip={Boolean(tooltip)} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{tooltip}</Typography>
      </DlMuiPopper>
    </>
  );
};

/** The least of the active label worth showing cut; below it the active tab drops its label like the others. */
const MIN_ACTIVE_LABEL_WIDTH = 40;

/**
 * Tab strip of a tabbed filter panel (PDM-2750): one pill per filter group - icon, label and a badge with the
 * number of active filters in the group. Built on MUI Tabs, so it keeps the app's tab colors and keyboard
 * handling; the size is the compact one of the "Filters" header row, not the 48px page tabs.
 *
 * Compact mode is automatic: when the strip with every label would not fit the width it gets, the INACTIVE tabs
 * drop their label (icon + badge only) while the active tab keeps it. The full-label width is measured on a
 * hidden copy of the strip, so the visible strip never flickers between the two states. If even the compact
 * strip is too wide, the active label is cut with an ellipsis to the width that is left (titled with the whole
 * label); only when less than [MIN_ACTIVE_LABEL_WIDTH] would be left (a very narrow pane) does the active tab drop
 * its label too (icon-only strip, every tab titled), and only if even that does not fit do the pills wrap onto a
 * second line rather than being clipped.
 */
export const LightFilterTabs: React.FC<Props> = (props) => {
  const {
    tabs,
    activeTab,
    onChange,
    dataName = 'light-filter-tabs',
    compact: forceCompact = false,
    showZeroCount = false,
    variant = 'filter',
    pageTabSize = false,
  } = props;
  const theme = useTheme();
  const muiStyles = getStyles({theme, params: {variant, pageTabSize}});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ghostRef = useRef<HTMLDivElement | null>(null);
  const ghostCompactRef = useRef<HTMLDivElement | null>(null);
  const [compact, setCompact] = useState(false);
  const [shrinkActive, setShrinkActive] = useState(false);
  const [iconOnly, setIconOnly] = useState(false);
  const measure = useCallback(() => {
    const container = containerRef.current;
    const ghost = ghostRef.current;
    const ghostCompact = ghostCompactRef.current;
    if (!container || !ghost || !ghostCompact) return;
    // The ghosts are the strip with all labels and the compact strip; +1 absorbs sub-pixel rounding of the flex layout.
    const width = container.clientWidth + 1;
    const compactWidth = ghostCompact.scrollWidth;
    // the compact ghost carries one label, the active tab's
    const activeLabelWidth = ghostCompact.querySelector<HTMLElement>('[data-name="light-filter-tabs--label"]')?.offsetWidth ?? 0;
    setCompact(ghost.scrollWidth > width);
    setShrinkActive(compactWidth > width);
    setIconOnly(compactWidth - activeLabelWidth + MIN_ACTIVE_LABEL_WIDTH > width);
  }, []);
  const labelsKey = `${activeTab}#${tabs.map((tab) => `${tab.id}:${tab.label}:${tab.count ?? 0}`).join('|')}`;
  useLayoutEffect(() => {
    measure();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
    // Re-measure when a label or a count changes width, not only on resize.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, labelsKey]);
  const renderTab = (tab: LightFilterTabDef, showLabel: boolean, ghost: boolean) => {
    const count = tab.count ?? 0;
    const IconComponent = getIconComponentByName(tab.iconName);
    const showBadge = count > 0 || showZeroCount;
    // a label cut by the ellipsis gets the tooltip like a dropped one
    const isLabelCut = !ghost && showLabel && shrinkActive && tab.id === activeTab;
    const hasTooltip = !ghost && (!showLabel || isLabelCut);
    return (
      <TooltipTab
        key={tab.id}
        tooltip={hasTooltip ? tab.label : undefined}
        value={tab.id}
        css={muiStyles.tab}
        icon={IconComponent ? <SvgIcon component={IconComponent} inheritViewBox css={muiStyles.tabIcon} /> : undefined}
        iconPosition='start'
        // An icon-only pill hides its empty label box, else the box and the gap before it push the icon off centre. The
        // box stays in the Tab though: only a Tab with an icon and a label wraps the icon in .MuiTab-iconWrapper, which the
        // theme colours (teal), the bare icon would take the dark text colour.
        label={
          <span css={muiStyles.tabLabel} style={showLabel || showBadge ? undefined : {display: 'none'}}>
            {showLabel && (
              <span css={muiStyles.tabLabelText} data-name='light-filter-tabs--label'>
                {tab.label}
              </span>
            )}
            {showBadge && (
              <span css={muiStyles.tabBadge} data-name='light-filter-tabs--badge' data-variant={variant}>
                {count}
              </span>
            )}
          </span>
        }
        aria-label={showLabel ? undefined : tab.label}
        onClick={ghost ? undefined : () => onChange(tab.id)}
        // The measuring copy must never take focus or clicks.
        disabled={ghost}
        tabIndex={ghost ? -1 : undefined}
        data-name={`${dataName}--tab`}
        data-tab-id={tab.id}
        style={tab.hidden ? {display: 'none'} : undefined}
      />
    );
  };
  return (
    <div
      ref={containerRef}
      css={muiStyles.container}
      data-name={dataName}
      data-variant={variant}
      data-compact={forceCompact || compact}
      data-shrink-active={shrinkActive && !iconOnly}
      data-icon-only={iconOnly}
    >
      <Tabs value={activeTab} css={muiStyles.tabs}>
        {tabs.map((tab) => renderTab(tab, !(forceCompact || compact) || (tab.id === activeTab && !iconOnly), false))}
      </Tabs>
      {/* Hidden copies used only for measuring - all labels, and the compact strip; not visible, not focusable, not announced.
          The clip keeps their natural width from overflowing the strip's box, which would otherwise scroll sideways. */}
      <div css={muiStyles.ghostClip} aria-hidden>
        <div ref={ghostRef} css={muiStyles.ghost}>
          <Tabs value={activeTab} css={muiStyles.tabs}>
            {tabs.map((tab) => renderTab(tab, true, true))}
          </Tabs>
        </div>
        <div ref={ghostCompactRef} css={muiStyles.ghost}>
          <Tabs value={activeTab} css={muiStyles.tabs}>
            {tabs.map((tab) => renderTab(tab, tab.id === activeTab, true))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};
LightFilterTabs.displayName = 'LightFilterTabs';

const getStyles: GetStylesFunction = (options) =>
  getVariantStyles(
    options?.params?.variant === 'panel',
    options?.params?.pageTabSize ? options?.theme?.typography?.cardHeader ?? PAGE_TAB_TYPOGRAPHY : undefined
  );

/** The theme's tab typography (MuiTab = cardHeader), for a theme without it. */
const PAGE_TAB_TYPOGRAPHY: React.CSSProperties = {fontFamily: 'SourceSansPro-Bold', fontSize: '18px', lineHeight: '23px'};

const getVariantStyles = (panel: boolean, pageTab: React.CSSProperties | undefined) => ({
  container: css`
    position: relative;
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    /* The compact strip still too wide: the active pill gives up the width it is short of, its label cut. */
    &[data-shrink-active='true'] .MuiTab-root.Mui-selected {
      flex-shrink: 1;
    }
    /* Last resort on a very narrow pane: wrap the icon-only pills instead of clipping them. */
    &[data-icon-only='true'] .MuiTabs-flexContainer {
      flex-wrap: wrap;
    }
  `,
  ghostClip: css`
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 0;
    overflow: hidden;
  `,
  ghost: css`
    position: absolute;
    top: 0;
    left: 0;
    visibility: hidden;
    pointer-events: none;
    white-space: nowrap;
    /* Natural width of the strip, unconstrained by the container. */
    width: max-content;
  `,
  // MUI Tabs default to 48px; this strip lives in the 32px "Filters" header row, so the pills define the height.
  tabs: css`
    min-height: 0;
    /* lets the strip be narrower than its content, so the active pill can shrink */
    min-width: 0;
    & .MuiTabs-scroller {
      overflow: visible !important;
    }
    & .MuiTabs-flexContainer {
      gap: ${panel ? 6 : 8}px;
      row-gap: ${panel ? 6 : 8}px;
    }
  `,
  // Pill: the app's tab shape (radius 8, teal when selected - from the theme) at the filter-header size. The
  // inactive fill is gray-50 like the page tabs (.dl-tab-row) and the icon buttons around it.
  tab: css`
    font-family: SourceSansPro-Semibold;
    font-size: ${panel ? '13.5px' : '14px'};
    line-height: 22px;
    padding: ${panel ? '8px' : '8px 12px'};
    min-height: 0;
    min-width: 0;
    gap: ${panel ? 7 : 8}px;
    border-radius: ${panel ? 9 : 8}px;
    background-color: var(--dlThemeGray-50);
    white-space: nowrap;
    &.Mui-selected {
      padding: 8px 12px;
    }
    &.Mui-disabled {
      opacity: 1;
    }
    & .MuiTab-iconWrapper {
      margin: 0;
    }
    ${pageTab
      ? `
    font-family: ${pageTab.fontFamily};
    font-size: ${pageTab.fontSize};
    line-height: ${pageTab.lineHeight};
    height: var(--horizontal-tab-height, 48px);
    padding-block: 0;
    &.Mui-selected {
      padding-block: 0;
    }
    `
      : ''}
  `,
  // on page-tab size the icon is as big as the text
  tabIcon: css`
    width: ${pageTab ? pageTab.fontSize : `${panel ? 19 : 14}px`};
    height: ${pageTab ? pageTab.fontSize : `${panel ? 19 : 14}px`};
    font-size: ${pageTab ? pageTab.fontSize : `${panel ? 19 : 14}px`};
  `,
  tabLabel: css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    & [data-name='light-filter-tabs--badge'] {
      flex-shrink: 0;
    }
  `,
  tabLabelText: css`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `,
  // Count badge: white on an inactive pill, translucent white on the selected (teal) one.
  tabBadge: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 6px;
    box-sizing: border-box;
    border-radius: 9px;
    font-family: SourceSansPro-Semibold;
    font-size: 11px;
    line-height: 18px;
    background: #ffffff;
    color: var(--dlTheme-600);
    .Mui-selected & {
      background: rgba(255, 255, 255, 0.22);
      color: #ffffff;
    }
    button:hover:not(.Mui-selected) & {
      background: rgba(255, 255, 255, 0.22);
      color: #ffffff;
    }
    /* the right-panel badge of the alerts design: less round, grey text on the white pill */
    &[data-variant='panel'] {
      border-radius: 5px;
      color: #4d5358;
    }
    .Mui-selected &[data-variant='panel'] {
      color: #ffffff;
    }
    ${pageTab
      ? `
    /* scaled with the label (13.5px -> the page tab's 18px, x4/3); the side padding a little less, so the icon-only strip
       with three 2-digit counts still fits the pane's minimum width (RIGHT_PANE_MIN_WIDTH) */
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    font-size: 14.5px;
    line-height: 24px;
    &[data-variant='panel'] {
      border-radius: 7px;
    }
    `
      : ''}
  `,
});
